import React, { useState, useEffect } from 'react';
import './App.css';
import QRScanner from './components/QRScanner';
import StatusCard from './components/StatusCard';
import TicketInfo from './components/TicketInfo';
import StepIndicator from './components/StepIndicator';
import ConfigSection from './components/ConfigSection';
import { switchQR, verifySecondaryQR, verifyQR1, verifyQR2, detectQRType } from './services/api';

function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [scannedQR1, setScannedQR1] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [status, setStatus] = useState({ show: false, type: '', icon: '', message: '', details: '' });
  const [instruction, setInstruction] = useState('📱 جاهز لمسح التذكرة');
  const [countdown, setCountdown] = useState(null);
  const [scannedQRCode, setScannedQRCode] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [apiUrl, setApiUrl] = useState(process.env.REACT_APP_API_URL || 'https://aboor-backend.onrender.com');
  const [autoResetCountdown, setAutoResetCountdown] = useState(null);
  const [timeoutWarning, setTimeoutWarning] = useState(false);

  const showStatus = (type, icon, message, details) => {
    setStatus({ show: true, type, icon, message, details });
  };

  const handleFirstScan = async (qrCode) => {
    setCurrentStep(1);
    showStatus('waiting', '⏳', 'جاري التحقق...', '');
    
    // Extract QR code from JSON if needed
    let qrString = qrCode;
    try {
      const parsed = JSON.parse(qrCode);
      if (parsed.qrCode) {
        qrString = parsed.qrCode;
      }
    } catch (e) {
      // Not JSON, use as is
    }
    
    setScannedQR1(qrString);
    
    // Detect QR code type
    const qrType = detectQRType(qrString);
    console.log('🔍 Detected QR Type:', qrType, 'for code:', qrString);
    
    try {
      let result;
      
      // Call the appropriate API based on QR code type
      if (qrType === 'qr1') {
        console.log('📱 Calling verify-qr1 endpoint');
        result = await verifyQR1(qrString, apiUrl);
      } else if (qrType === 'qr2') {
        console.log('📱 Calling verify-qr2 endpoint');
        result = await verifyQR2(qrString, apiUrl);
      } else {
        // Fallback to legacy endpoint for unknown format
        console.log('📱 Calling legacy switch-qr endpoint');
        result = await switchQR(qrString, apiUrl);
      }
      
      if (result.success) {
        setTicketData(result.data);
        showStatus('success', '✅', 'تم المسح', 'QR Code 1 verified');
        setCurrentStep(2);
        startCountdown();
      } else {
        showStatus('error', '❌', 'فشل التحقق', result.message || 'رمز غير صالح');
        setTimeout(() => {
          setIsPaused(false);
        }, 3000);
      }
    } catch (error) {
      console.error('API Error:', error);
      const errorMessage = error.message || 'تأكد من تشغيل الخادم';
      showStatus('error', '❌', 'خطأ', errorMessage);
      setTimeout(() => {
        setIsPaused(false);
      }, 3000);
    }
  };

  const startCountdown = () => {
    setInstruction('⏳ انتظر قليلاً...');
    
    let count = 3;
    setCountdown(count);
    
    const interval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(interval);
        setCountdown(null);
        setInstruction('👉 جاهز لمسح QR Code 2');
        setCurrentStep(3);
        showStatus('waiting', '📱', 'جاهز للمسح الثاني', 'Scan QR Code 2');
        setIsPaused(false);
        
        // Start timeout warning after 30 seconds
        startTimeoutWarning(30);
      }
    }, 1000);
  };

  const startAutoReset = (seconds) => {
    let count = seconds;
    setAutoResetCountdown(count);
    
    const interval = setInterval(() => {
      count--;
      if (count > 0) {
        setAutoResetCountdown(count);
      } else {
        clearInterval(interval);
        setAutoResetCountdown(null);
        resetScanner();
      }
    }, 1000);
  };

  const startTimeoutWarning = (seconds) => {
    setTimeout(() => {
      if (currentStep === 3 && !timeoutWarning) {
        setTimeoutWarning(true);
        showStatus('warning', '⏰', 'انتهت المهلة', 'Timeout - please rescan');
        
        // Auto-reset after 10 more seconds
        setTimeout(() => {
          resetScanner();
        }, 10000);
      }
    }, seconds * 1000);
  };

  const handleSecondScan = async (qrCode) => {
    setCurrentStep(3);
    showStatus('waiting', '⏳', 'جاري التحقق...', '');
    
    // Extract QR code from JSON if needed
    let qrString = qrCode;
    try {
      const parsed = JSON.parse(qrCode);
      if (parsed.qrCode) {
        qrString = parsed.qrCode;
      }
    } catch (e) {
      // Not JSON, use as is
    }
    
    // Check if same as first QR (screenshot detection!)
    if (qrString === scannedQR1) {
      showStatus('error', '🚨', 'خطأ', 'Same QR code scanned');
      return;
    }
    
    // Detect QR code type
    const qrType = detectQRType(qrString);
    console.log('🔍 Detected QR Type for second scan:', qrType, 'for code:', qrString);
    
    try {
      let result;
      
      // Call the appropriate API based on QR code type
      if (qrType === 'qr1') {
        console.log('📱 Calling verify-qr1 endpoint');
        result = await verifyQR1(qrString, apiUrl);
      } else if (qrType === 'qr2') {
        console.log('📱 Calling verify-qr2 endpoint');
        result = await verifyQR2(qrString, apiUrl);
      } else {
        // Fallback to legacy endpoint for unknown format
        console.log('📱 Calling legacy verify-secondary-qr endpoint');
        result = await verifySecondaryQR(qrString, apiUrl);
      }
      
      if (result.success) {
        showStatus('success', '✅', 'تم الدخول', 'Entry granted');
        setTicketData(prev => ({ ...prev, ...result.data }));
        setInstruction('✅ يمكن الدخول الآن');
        
        // Auto-reset after 5 seconds
        startAutoReset(5);
      } else {
        showStatus('error', '❌', 'فشل', result.message || 'Invalid');
      }
    } catch (error) {
      console.error('API Error:', error);
      const errorMessage = error.message || 'تأكد من تشغيل الخادم';
      showStatus('error', '❌', 'خطأ', errorMessage);
    }
  };

  const handleScan = (qrCode) => {
    setScannedQRCode(qrCode);
    setIsPaused(true);
    
    if (currentStep === 1) {
      handleFirstScan(qrCode);
    } else if (currentStep === 3) {
      handleSecondScan(qrCode);
    }
  };

  const resetScanner = () => {
    console.log('🔄 Resetting scanner to initial state');
    setCurrentStep(1);
    setScannedQR1(null);
    setTicketData(null);
    setStatus({ show: false, type: '', icon: '', message: '', details: '' });
    setInstruction('📱 جاهز لمسح التذكرة');
    setCountdown(null);
    setScannedQRCode('');
    setIsPaused(false);
    setAutoResetCountdown(null);
    setTimeoutWarning(false);
  };

  const toggleManualInput = () => {
    setShowManualInput(!showManualInput);
    setIsPaused(!showManualInput);
  };

  return (
    <div className="container">
      <div className="header">
        <h1>🎫 Aboor Scanner</h1>
        <p className="subtitle">نظام التحقق من التذاكر - Anti-Screenshot Security</p>
      </div>

      <ConfigSection apiUrl={apiUrl} setApiUrl={setApiUrl} />

      <StepIndicator currentStep={currentStep} />

      <div className="instruction">{instruction}</div>

      <div style={{ textAlign: 'center', marginBottom: '15px' }}>
        <button 
          className="button button-secondary" 
          onClick={toggleManualInput}
          style={{ padding: '10px 20px', fontSize: '14px' }}
        >
          {showManualInput ? '📷 استخدام الكاميرا' : '⌨️ إدخال يدوي'}
        </button>
      </div>

      <QRScanner 
        onScan={handleScan}
        isPaused={isPaused}
        showManualInput={showManualInput}
      />

      <div className="scanning-tips">
        <h4>💡 نصائح للمسح الناجح:</h4>
        <ul>
          <li>أمسك الهاتف بثبات على بعد 15-30 سم</li>
          <li>تأكد من وجود إضاءة كافية</li>
          <li>اجعل الرمز داخل المربع بالكامل</li>
          <li>انتظر ثانية واحدة للتركيز التلقائي</li>
          <li>إذا فشل المسح، استخدم الإدخال اليدوي</li>
        </ul>
      </div>

      <StatusCard status={status} />

      {countdown !== null && (
        <div className="countdown">{countdown}</div>
      )}

      {autoResetCountdown !== null && (
        <div className="countdown" style={{ color: '#10b981' }}>
          إعادة تعيين تلقائية خلال: {autoResetCountdown}
        </div>
      )}

      {scannedQRCode && (
        <div className="qr-display">{scannedQRCode}</div>
      )}

      <TicketInfo ticketData={ticketData} />

      {(status.type === 'success' || status.type === 'error' || timeoutWarning) && currentStep === 3 && (
        <button className="button button-secondary" onClick={resetScanner}>
          🔄 فحص تذكرة جديدة
        </button>
      )}
    </div>
  );
}

export default App;
